'use client';

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import { useRouter, Link } from '../../../i18n/routing';
import { Eye, EyeOff } from 'lucide-react';
import { Turnstile } from '@marsidev/react-turnstile';

/** Sign in with Apple: planned for a future request; set true when backend and env are ready. */
const SIGN_IN_WITH_APPLE_ENABLED = false;

const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M23.52 12.29c0-.85-.08-1.68-.22-2.48H12v4.69h6.46c-.28 1.49-1.12 2.75-2.38 3.59v2.99h3.85c2.25-2.07 3.55-5.12 3.55-8.79z" fill="#4285F4"/>
    <path d="M12 24c3.24 0 5.96-1.07 7.94-2.9l-3.85-2.99c-1.07.72-2.44 1.15-4.09 1.15-3.13 0-5.78-2.11-6.73-4.96H1.31v3.12C3.31 21.37 7.37 24 12 24z" fill="#34A853"/>
    <path d="M5.27 14.29c-.24-.72-.37-1.49-.37-2.29s.13-1.57.37-2.29V6.59H1.31C.47 8.24 0 10.07 0 12c0 1.93.47 3.76 1.31 5.41l3.96-3.12z" fill="#FBBC05"/>
    <path d="M12 4.79c1.76 0 3.34.61 4.59 1.8l3.43-3.43C17.95 1.19 15.24 0 12 0 7.37 0 3.31 2.63 1.31 6.59l3.96 3.12c.95-2.85 3.6-4.96 6.73-4.96z" fill="#EA4335"/>
  </svg>
);

export default function RegisterPage() {
  const t = useTranslations();
  const locale = useLocale();
  const router = useRouter();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  
  // Field validation errors
  const [fieldErrors, setFieldErrors] = useState<{
    fullName?: string;
    email?: string;
    password?: string;
    passwordConfirm?: string;
    terms?: string;
    turnstile?: string;
  }>({});
  
  const [snackbar, setSnackbar] = useState<{
    show: boolean;
    message: string;
    color: 'success' | 'error' | 'warning' | 'info';
  }>({
    show: false,
    message: '',
    color: 'info',
  });

  // Check if Turnstile is required (only if site key is configured AND not in dev environment)
  const turnstileConfig = (() => {
    // Check for site key from both build-time and runtime config
    // ConfigMap may have NEXT_PUBLIC_CLOUDFLARE_SITE_KEY (could be empty), 
    // but the actual key is NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY from secret (embedded at build time)
    let siteKey: string | undefined;
    
    if (typeof window !== 'undefined' && window._ENV_) {
      // Check runtime config first (but skip if empty string)
      const runtimeKey = window._ENV_.NEXT_PUBLIC_CLOUDFLARE_SITE_KEY || window._ENV_.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY;
      if (runtimeKey && runtimeKey.trim()) {
        siteKey = runtimeKey;
      }
    }
    
    // Fallback to build-time env (embedded at build time from secret)
    if (!siteKey) {
      siteKey = process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY;
    }
    
    if (!siteKey) {
      return { required: false, siteKey: undefined };
    }
    
    // Check environment from both build-time and runtime config
    const env = typeof window !== 'undefined' && window._ENV_?.NEXT_PUBLIC_ENV
      ? window._ENV_.NEXT_PUBLIC_ENV
      : process.env.NEXT_PUBLIC_ENV;
    
    // Default to requiring Turnstile if site key exists (safer for production)
    // Only disable if explicitly in development
    const isDev = env === 'dev' || env === 'development';
    // If env is not set or not 'dev'/'development', require Turnstile
    const required = !isDev;
    
    return { required, siteKey };
  })();

  const isTurnstileRequired = turnstileConfig.required;
  const turnstileSiteKey = turnstileConfig.siteKey;


  // Check for error query parameter on mount
  useEffect(() => {
    setMounted(true);
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const error = params.get('error');
      if (error) {
        const errorMessage = getErrorMessage(error);
        setSnackbar({
          show: true,
          message: errorMessage,
          color: 'error',
        });
        router.replace('/register');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getErrorMessage = (errorCode: string): string => {
    const errorMap: Record<string, string> = {
      'register_failed': t('registerPage.registerError'),
      'email_exists': t('registerPage.registerError'),
    };
    return errorMap[errorCode] || t('registerPage.registerError');
  };

  const validateFullName = (value: string): boolean => {
    return !!value && value.trim().length > 0;
  };

  const validateEmail = (value: string): boolean => {
    return !!value && /.+@.+\..+/.test(value);
  };

  const validatePassword = (value: string): boolean => {
    return !!value && value.length >= 6;
  };

  const validatePasswordMatch = (password: string, confirmPassword: string): boolean => {
    return !!confirmPassword && password === confirmPassword;
  };

  // Validate all fields and update error state
  const validateForm = (): boolean => {
    const errors: typeof fieldErrors = {};
    let isValid = true;

    if (!validateFullName(fullName)) {
      errors.fullName = t('registerPage.fullNameRequired');
      isValid = false;
    }

    if (!validateEmail(email)) {
      errors.email = t('registerPage.emailInvalid');
      isValid = false;
    }

    if (!validatePassword(password)) {
      errors.password = t('registerPage.passwordMinLength');
      isValid = false;
    }

    if (!validatePasswordMatch(password, passwordConfirm)) {
      errors.passwordConfirm = t('registerPage.passwordMismatch');
      isValid = false;
    }

    if (!acceptTerms) {
      errors.terms = t('registerPage.acceptTermsRequired');
      isValid = false;
    }

    // Only require Turnstile if it's configured
    if (isTurnstileRequired && !turnstileToken) {
      errors.turnstile = 'Please complete the security verification';
      isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  // Check if form is valid (for button enable/disable)
  const isFormValid = (): boolean => {
    return (
      validateFullName(fullName) &&
      validateEmail(email) &&
      validatePassword(password) &&
      validatePasswordMatch(password, passwordConfirm) &&
      acceptTerms &&
      (!isTurnstileRequired || !!turnstileToken)
    );
  };

  const handleRegister = async () => {
    if (loading) return;

    // Validate form and show errors
    if (!validateForm()) {
      const firstError = Object.values(fieldErrors)[0];
      if (firstError) {
        showSnackbar(firstError, 'warning');
      }
      return;
    }

    setLoading(true);

    try {
      const { register } = await import('@/actions/auth');
      const result = await register(fullName, email, password, locale, turnstileToken);

      if (result.success) {
        showSnackbar(t('registerPage.registerSuccess'), 'success');
        setTimeout(() => {
          router.push('/login');
        }, 2000);
      } else {
        const errorMessage = result.error || t('registerPage.registerError');
        showSnackbar(errorMessage, 'error');
      }
    } catch (error) {
      console.error('Registration API call failed:', error);
      showSnackbar(t('registerPage.registerError'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const registerWithGoogle = () => {
    const returnUrl = encodeURIComponent(
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000'
    );
    const googleLoginUrl = `/api/auth/google/login?returnUrl=${returnUrl}`;
    window.location.href = googleLoginUrl;
  };

  const registerWithApple = () => {
    const returnUrl = encodeURIComponent(
      typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000'
    );
    const appleLoginUrl = `/api/auth/apple/login?returnUrl=${returnUrl}`;
    window.location.href = appleLoginUrl;
  };

  const showSnackbar = (message: string, color: 'success' | 'error' | 'warning' | 'info') => {
    setSnackbar({
      show: true,
      message,
      color,
    });
    // Auto-hide after 4 seconds
    setTimeout(() => {
        setSnackbar(prev => ({ ...prev, show: false }));
    }, 4000);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, show: false });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRegister();
    }
  };

  // Update field errors on change
  const handleFullNameChange = (value: string) => {
    setFullName(value);
    if (fieldErrors.fullName) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        if (validateFullName(value)) {
          delete newErrors.fullName;
        } else {
          newErrors.fullName = t('registerPage.fullNameRequired');
        }
        return newErrors;
      });
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (fieldErrors.email) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        if (validateEmail(value)) {
          delete newErrors.email;
        } else {
          newErrors.email = t('registerPage.emailInvalid');
        }
        return newErrors;
      });
    }
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      if (validatePassword(value)) {
        delete newErrors.password;
      } else {
        newErrors.password = t('registerPage.passwordMinLength');
      }
      // Also check password match when password changes
      if (passwordConfirm && !validatePasswordMatch(value, passwordConfirm)) {
        newErrors.passwordConfirm = t('registerPage.passwordMismatch');
      } else if (passwordConfirm && validatePasswordMatch(value, passwordConfirm)) {
        delete newErrors.passwordConfirm;
      }
      return newErrors;
    });
  };

  const handlePasswordConfirmChange = (value: string) => {
    setPasswordConfirm(value);
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      if (validatePasswordMatch(password, value)) {
        delete newErrors.passwordConfirm;
      } else {
        newErrors.passwordConfirm = t('registerPage.passwordMismatch');
      }
      return newErrors;
    });
  };

  const handleTermsChange = (checked: boolean) => {
    setAcceptTerms(checked);
    if (fieldErrors.terms) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        if (checked) {
          delete newErrors.terms;
        } else {
          newErrors.terms = t('registerPage.acceptTermsRequired');
        }
        return newErrors;
      });
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 font-sans">
      {/* LEFT SIDE: Image with Overlay */}
      <div className="hidden lg:flex relative min-h-screen overflow-hidden">
        <Image
          src="/login-bg.jpg"
          alt="Sailing adventure"
          fill
          priority
          className="object-cover"
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/40 to-black/50" />
        {/* Quote Overlay */}
        <div className="relative z-10 flex flex-col justify-center items-center p-12 text-white">
          <h2 className="text-4xl lg:text-5xl font-bold mb-4 text-center leading-tight">
            {t('loginPage.oceanQuote')}
          </h2>
          <p className="text-lg lg:text-xl text-white/90 text-center max-w-md">
            {t('loginPage.heroSubheadline')}
          </p>
        </div>
      </div>

      {/* RIGHT SIDE: Form */}
      <div className="flex flex-col justify-center items-center p-6 sm:p-8 lg:p-12 bg-white dark:bg-slate-50 min-h-screen">
        <div className="w-full max-w-md">
          {/* Mobile Header Image */}
          <div className="lg:hidden relative h-48 mb-8 rounded-xl overflow-hidden">
            <Image
              src="/login-bg.jpg"
              alt="Sailing adventure"
              fill
              className="object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/60" />
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <h2 className="text-2xl font-bold text-white text-center">
                {t('loginPage.oceanQuote')}
              </h2>
            </div>
          </div>

          {/* Form Card */}
          <div className="w-full">
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-900 mb-2">
                {t('registerPage.title')}
              </h1>
              <p className="text-slate-600 dark:text-slate-600">
                {t('loginPage.heroSubheadline')}
              </p>
            </div>

            {/* OAuth Buttons Top */}
            <div className="flex flex-col gap-3 mb-6">
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="px-3 py-1 bg-blue-100 dark:bg-blue-50 text-blue-700 dark:text-blue-700 text-xs font-bold rounded-full uppercase tracking-wide">
                  {t('loginPage.fastAndSecure')}
                </span>
              </div>

              <button
                onClick={registerWithGoogle}
                className="flex items-center justify-center gap-3 py-3 px-4 bg-white dark:bg-white border border-slate-200 dark:border-slate-300 text-slate-700 dark:text-slate-700 font-semibold rounded-xl hover:bg-slate-50 dark:hover:bg-slate-50 transition-all shadow-sm hover:shadow-md text-base"
              >
                {mounted && <GoogleIcon />}
                <span>{t('loginPage.loginWithGoogle')}</span>
              </button>

              {SIGN_IN_WITH_APPLE_ENABLED && (
                <button
                  onClick={registerWithApple}
                  className="flex items-center justify-center gap-3 py-3 px-4 bg-black hover:bg-zinc-800 text-white font-semibold rounded-xl transition-all shadow-sm hover:shadow-md text-base"
                >
                  {mounted && <AppleIcon />}
                  <span>{t('loginPage.loginWithApple')}</span>
                </button>
              )}
            </div>

            {/* Separator */}
            <div className="relative flex items-center mb-6">
              <div className="flex-grow border-t border-slate-300 dark:border-slate-300"></div>
              <span className="flex-shrink-0 mx-4 text-xs font-medium text-slate-500 dark:text-slate-500 uppercase tracking-wider">
                {t('loginPage.orSeparatorLowercase')}
              </span>
              <div className="flex-grow border-t border-slate-300 dark:border-slate-300"></div>
            </div>

            <div className="flex flex-col gap-5">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-700">
                  {t('registerPage.fullNameLabel')}
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => handleFullNameChange(e.target.value)}
                  onKeyDown={handleKeyPress}
                  required
                  autoComplete="name"
                  className={`w-full px-4 py-3 bg-white dark:bg-white border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-900 dark:text-slate-900 placeholder:text-slate-400 ${
                    fieldErrors.fullName ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-300'
                  }`}
                />
                {fieldErrors.fullName && (
                  <p className="text-sm text-red-500 mt-1">{fieldErrors.fullName}</p>
                )}
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-700">
                  {t('registerPage.emailLabel')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  onKeyDown={handleKeyPress}
                  required
                  autoComplete="email"
                  className={`w-full px-4 py-3 bg-white dark:bg-white border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-slate-900 dark:text-slate-900 placeholder:text-slate-400 ${
                    fieldErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-300'
                  }`}
                />
                {fieldErrors.email && (
                  <p className="text-sm text-red-500 mt-1">{fieldErrors.email}</p>
                )}
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-700">
                  {t('registerPage.passwordLabel')}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => handlePasswordChange(e.target.value)}
                    onKeyDown={handleKeyPress}
                    required
                    autoComplete="new-password"
                    className={`w-full px-4 py-3 bg-white dark:bg-white border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all pr-10 text-slate-900 dark:text-slate-900 placeholder:text-slate-400 ${
                      fieldErrors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-700"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {fieldErrors.password && (
                  <p className="text-sm text-red-500 mt-1">{fieldErrors.password}</p>
                )}
              </div>

              <div className="space-y-1">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-700">
                  {t('registerPage.confirmPasswordLabel')}
                </label>
                <div className="relative">
                  <input
                    type={showPasswordConfirm ? 'text' : 'password'}
                    value={passwordConfirm}
                    onChange={(e) => handlePasswordConfirmChange(e.target.value)}
                    onKeyDown={handleKeyPress}
                    required
                    autoComplete="new-password"
                    className={`w-full px-4 py-3 bg-white dark:bg-white border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all pr-10 text-slate-900 dark:text-slate-900 placeholder:text-slate-400 ${
                      fieldErrors.passwordConfirm ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-300 dark:border-slate-300'
                    }`}
                    placeholder="Re-enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 dark:text-slate-500 dark:hover:text-slate-700"
                  >
                    {showPasswordConfirm ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {fieldErrors.passwordConfirm && (
                  <p className="text-sm text-red-500 mt-1">{fieldErrors.passwordConfirm}</p>
                )}
              </div>

              {/* Terms & Privacy Checkbox */}
              <div className="flex items-start gap-3 pt-2">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  checked={acceptTerms}
                  onChange={(e) => handleTermsChange(e.target.checked)}
                  className={`mt-1 h-4 w-4 rounded text-blue-600 focus:ring-blue-500 ${
                    fieldErrors.terms ? 'border-red-500' : 'border-slate-300'
                  }`}
                />
                <label htmlFor="acceptTerms" className="text-sm text-slate-600 dark:text-slate-600 leading-relaxed">
                  {t('registerPage.acceptTerms')}{' '}
                  <Link href="/terms" className="text-blue-600 dark:text-blue-600 font-semibold hover:underline">
                    {t('registerPage.termsOfService')}
                  </Link>
                  {' '}{t('registerPage.and')}{' '}
                  <Link href="/privacy" className="text-blue-600 dark:text-blue-600 font-semibold hover:underline">
                    {t('registerPage.privacyPolicy')}
                  </Link>
                </label>
              </div>
              {fieldErrors.terms && (
                <p className="text-sm text-red-500 -mt-3">{fieldErrors.terms}</p>
              )}

              {/* Activation Token Info */}
              <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-50 border border-blue-200 dark:border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-800">
                  <span className="font-semibold">ℹ️ </span>
                  {t('registerPage.activationTokenInfo')}
                </p>
              </div>

              {/* Cloudflare Turnstile */}
              {isTurnstileRequired && turnstileSiteKey && (
                <div className="flex flex-col items-center mt-4">
                  <p className="text-sm text-slate-600 dark:text-slate-600 mb-2">
                    Complete the verification below to enable the Register button.
                  </p>
                  <Turnstile
                    siteKey={turnstileSiteKey}
                    onSuccess={(token) => {
                      setTurnstileToken(token);
                      setFieldErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors.turnstile;
                        return newErrors;
                      });
                    }}
                    onError={() => {
                      setTurnstileToken(null);
                      setFieldErrors(prev => ({
                        ...prev,
                        turnstile: 'Security verification failed. Please try again.',
                      }));
                    }}
                    onExpire={() => {
                      setTurnstileToken(null);
                      setFieldErrors(prev => ({
                        ...prev,
                        turnstile: 'Security verification expired. Please complete it again.',
                      }));
                    }}
                  />
                  {fieldErrors.turnstile && (
                    <p className="text-sm text-red-500 mt-2">{fieldErrors.turnstile}</p>
                  )}
                </div>
              )}

              <button
                onClick={handleRegister}
                disabled={loading || !isFormValid()}
                className="w-full py-3 px-4 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 focus:ring-4 focus:ring-blue-500/30 transition-all disabled:opacity-70 disabled:cursor-not-allowed mt-2 flex items-center justify-center shadow-lg hover:shadow-blue-500/25"
              >
                {loading ? (
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : null}
                {loading ? t('common.loading') : t('registerPage.registerButton')}
              </button>
            </div>

            <p className="text-center text-sm text-slate-600 dark:text-slate-600 mt-6">
              {t('registerPage.loginPrompt')}{' '}
              <Link href="/login" className="text-blue-600 dark:text-blue-600 font-bold hover:underline">
                {t('registerPage.loginButton')}
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Snackbar - Rendered via Portal */}
      {mounted && snackbar.show && createPortal(
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] animate-in fade-in slide-in-from-top-2 duration-300 w-full max-w-sm px-4">
          <div className={`px-4 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center justify-between ${
            snackbar.color === 'error' ? 'bg-red-50 text-red-800 border border-red-200 dark:bg-red-50 dark:text-red-800 dark:border-red-200' :
            snackbar.color === 'success' ? 'bg-green-50 text-green-800 border border-green-200 dark:bg-green-50 dark:text-green-800 dark:border-green-200' :
            snackbar.color === 'warning' ? 'bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-50 dark:text-amber-800 dark:border-amber-200' :
            'bg-blue-50 text-blue-800 border border-blue-200 dark:bg-blue-50 dark:text-blue-800 dark:border-blue-200'
          }`}>
            <span>{snackbar.message}</span>
            <button
              onClick={handleCloseSnackbar}
              className="ml-2 hover:opacity-70 p-1"
            >
              ✕
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
